<template>
  <div
    :class="[BASE, $slots.secondary ? 'md:justify-between' : 'md:justify-end']"
    data-cy="action-bar"
  >
    <div v-if="$slots.secondary" class="w-full md:w-auto">
      <slot name="secondary"></slot>
    </div>
    <div
      class="flex flex-col-reverse md:flex-row items-stretch md:items-center gap-3 w-full md:w-auto"
    >
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
// Shared sticky action bar for forms: keeps a form's primary action pinned to
// the bottom of the page scroll container instead of leaving it to scroll away
// mid-page.
//
// MUST render a single root element. Vue 3 does not apply a parent's scopeId to
// a fragment root, so a multi-root version would silently drop any scoped style
// a consumer targets at this component -- failing invisibly, at runtime, only
// for the consumers that style it.
//
// The overhang and the inner padding are sized independently, and it is easy to
// conflate them:
//
// - OVERHANG (`bottom`) is how far the bar's box extends past its container's
//   content box, and it must equal the container's full bottom padding --
//   --sera-main-padding PLUS the safe-area inset -- or a band of container that
//   width shows below the bar at every scroll position.
// - INNER PADDING (`pb`) is measured from the bar's own bottom edge, which the
//   overhang has already placed at the container's padding edge. So it does NOT
//   compensate for the overhang; adding --sera-main-padding here again just
//   makes the bar bottom-heavy against its `pt-4`.
//
//   It is max(), not a sum. The inset is a band the platform has already
//   reserved for the home indicator, so it doubles as breathing room rather
//   than needing 1rem added on top: iPadOS reports 25px, and 1rem + 25px gave
//   41px of white under the buttons, which reads as a mistake even though every
//   term is defensible. max() keeps 1rem where there is no cutout, and lets the
//   reserved band do the job where there is one -- always clearing the
//   indicator, never paying twice.
//
// z-20 keeps this in the page-content band: below the mobile nav drawer
// (z-[1001], Sidebar.vue) and everything above it in TB-2193's band map, and
// above ordinary page content. It matches what the hand-rolled bars this
// replaces already used, so stacking against existing pages is unchanged.

// `$slots.secondary` is tested in the template, not a computed: a consumer that
// provides the slot conditionally (`<template v-if="..." #secondary>`) changes
// the slot's presence between renders, and reading it in the render function is
// what reliably tracks that. A computed over useSlots() can go stale.
//
// MOUNT AS A DIRECT CHILD OF THE PAGE ROOT, not nested inside a padded
// container. The overhang below is sized to `.layout-1`'s padding, which is all
// it can know about; any *additional* padding on an ancestor is padding this bar
// cannot cover, and it shows as a strip of that ancestor's background below the
// bar once the container is scrolled to the bottom (where a sticky element drops
// to its flow position and stops overhanging). Same for horizontal padding,
// which leaves content visible either side of the bar mid-scroll.
//
// The bar overhangs its scroll container's bottom padding so its background
// reaches the container edge -- without this, content scrolls into the gap
// underneath it. The overhang and the matching inner padding are both derived
// from --sera-main-padding rather than hardcoded, so they track `.layout-1`'s
// padding instead of drifting from it.
const BASE =
  "sticky bottom-[calc(-1*(var(--sera-main-padding)+env(safe-area-inset-bottom,0px)))] " +
  "z-20 w-full bg-white border-t border-solid " +
  "pt-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] " +
  "flex gap-4 flex-col-reverse md:flex-row items-stretch md:items-center";
</script>
